import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { Divider, Typography } from '@material-ui/core';
import { isMissedFinding, getOriginLabelKeys } from '../../../../../../helpers/diagnostics/detectionOrigin';

const matchesClassValue = (item, classValue) => (item?.finding_class || item?.class) === classValue;

export const formatBiradsLabel = (value, t, { smallScreen = false } = {}) => {
  if (!value) return '';
  if (value === 'lesionKnown') {
    return smallScreen ? t('projections.lesionKnownShort') : t('projections.lesionKnown');
  }
  if (value === 'vessel') {
    return smallScreen ? 'V' : t('projections.vessels');
  }
  const level = value?.slice(6, 7);
  return smallScreen ? level : t(`revision.${value}`);
};

export const getLesionClass = (prevClass, t) =>
  prevClass === 'lesionKnown' ? t('projections.lesionKnown') : `BI-RADS ${prevClass?.slice(6, 7)}`;

// Tooltip functions
const classTooltip = (option, biradsClass, severityChange, isClustered, t, dateExam) => {
  const examPeriod = t(`diagnose.${option}`);

  if (isClustered) return t('diagnose.is_clustered');
  else if (severityChange === 0) return t('diagnose.class_stable') + ` ↔ `;
  else if (severityChange > 0)
    return (
      `↑ ` +
      t('diagnose.severity_change_up', {
        examOption: examPeriod,
        severityChange: severityChange,
        dateExam: dateExam,
      })
    );
  else if (severityChange < 0)
    return (
      `↓ ` +
      t('diagnose.severity_change_down', {
        examOption: examPeriod,
        severityChange: severityChange,
        dateExam: dateExam,
      })
    );
  else return `${t(`revision.${biradsClass}`)}`;
};

export const sizeChangeTooltip = (option, sizeChange, dateFirst, t) => {
  const examPeriod = t(`diagnose.${option}`);

  switch (true) {
    case sizeChange === undefined || sizeChange === null:
      return '';
    case sizeChange < -5:
      return t('diagnose.size_change_down', {
        examOption: examPeriod,
        sizePercentage: sizeChange?.toFixed(1),
        dateExam: dateFirst,
      });
    case sizeChange >= -5 && sizeChange < 5:
      return t('diagnose.size_stable', {
        examOption: examPeriod,
        sizePercentage: sizeChange?.toFixed(1),
        dateExam: dateFirst,
      });
    case sizeChange >= 5 && sizeChange < 20:
      return t('diagnose.size_change_up_slow', {
        examOption: examPeriod,
        sizePercentage: sizeChange?.toFixed(1),
        dateExam: dateFirst,
      });
    case sizeChange >= 20 && sizeChange < 50:
      return t('diagnose.size_change_up_moderate', {
        examOption: examPeriod,
        sizePercentage: sizeChange?.toFixed(1),
        dateExam: dateFirst,
      });
    case sizeChange >= 50:
      return t('diagnose.size_change_up_fast', {
        examOption: examPeriod,
        sizePercentage: sizeChange?.toFixed(1),
        dateExam: dateFirst,
      });
    default:
      return t('diagnose.size_stable', {
        examOption: examPeriod,
        sizePercentage: sizeChange?.toFixed(1) || '',
        dateExam: dateFirst || '',
      });
  }
};

// Size change icon component
export const sizeChangeIcon = (sizeChange, growthCategory, classes) => {
  if (sizeChange === undefined) return null;

  const commonProps = {
    fontSize: 'small',
    className: classes.size_change_icon,
    style: { color: 'white', height: '12px' },
  };

  if (sizeChange < 0 || growthCategory === 'decreased') {
    return <ArrowDownwardIcon {...commonProps} />;
  }

  const icons = {
    stable: <TrendingFlatIcon {...commonProps} />,
    slow: <ArrowUpwardIcon {...commonProps} style={{ ...commonProps.style, transform: 'rotate(80deg)' }} />,
    moderate: (
      <ArrowUpwardIcon {...commonProps} style={{ ...commonProps.style, transform: 'rotate(50deg)' }} />
    ),
    fast: <ArrowUpwardIcon {...commonProps} />,
  };

  return icons[growthCategory] || <TrendingFlatIcon {...commonProps} />;
};

// Helper function to format BI-RADS range
export const formatBiradsRange = (biradsArray) => {
  if (!Array.isArray(biradsArray) || biradsArray.length === 0) return '';

  const numbers = biradsArray
    .filter((birads) => birads.startsWith('birads'))
    .map((birads) => parseInt(birads.replace('birads', '')))
    .filter((num) => !isNaN(num))
    .sort((a, b) => a - b);

  if (numbers.length === 0) return '';

  const lowest = numbers[0];
  const highest = numbers[numbers.length - 1];

  return lowest === highest ? `${lowest}` : `${lowest} / ${highest}`;
};

// Show overlays based on filter criteria (type and class)
export const showFilteredOverlays = (typeValue, classValue, dispatch, actions) => {
  const allProjections = ['rcc', 'rmlo', 'lcc', 'lmlo'];

  // Microcalc classes
  const microcalcClasses = ['vessel', 'birads2', 'birads3', 'birads4', 'birads5'];
  // Opacities classes
  const opacitiesClasses = ['birads2', 'birads3', 'birads4', 'birads5', 'lesionKnown'];

  // Initialize all overlays as hidden
  const microcalcOverlays = {
    vessel: Object.fromEntries(allProjections.map((p) => [p, false])),
    birads2: Object.fromEntries(allProjections.map((p) => [p, false])),
    birads3: Object.fromEntries(allProjections.map((p) => [p, false])),
    birads4: Object.fromEntries(allProjections.map((p) => [p, false])),
    birads5: Object.fromEntries(allProjections.map((p) => [p, false])),
  };

  const opacitiesOverlays = {
    birads2: Object.fromEntries(allProjections.map((p) => [p, false])),
    birads3: Object.fromEntries(allProjections.map((p) => [p, false])),
    birads4: Object.fromEntries(allProjections.map((p) => [p, false])),
    birads5: Object.fromEntries(allProjections.map((p) => [p, false])),
    lesionKnown: Object.fromEntries(allProjections.map((p) => [p, false])),
  };

  // Show overlays based on filter
  if (typeValue === 'microcalc') {
    // If class filter is 'all', show all microcalc classes
    if (classValue === 'all') {
      microcalcClasses.forEach((cls) => {
        microcalcOverlays[cls] = Object.fromEntries(allProjections.map((p) => [p, true]));
      });
    } else if (microcalcClasses.includes(classValue)) {
      // Show only the selected class
      microcalcOverlays[classValue] = Object.fromEntries(allProjections.map((p) => [p, true]));
    }
  } else if (typeValue === 'opacities') {
    // If class filter is 'all', show all opacities classes
    if (classValue === 'all') {
      opacitiesClasses.forEach((cls) => {
        opacitiesOverlays[cls] = Object.fromEntries(allProjections.map((p) => [p, true]));
      });
    } else if (opacitiesClasses.includes(classValue)) {
      // Show only the selected class
      opacitiesOverlays[classValue] = Object.fromEntries(allProjections.map((p) => [p, true]));
    }
  } else if (typeValue === 'all') {
    // If type is 'all' but class is selected, show that class for both types
    if (classValue !== 'all') {
      if (microcalcClasses.includes(classValue)) {
        microcalcOverlays[classValue] = Object.fromEntries(allProjections.map((p) => [p, true]));
      }
      if (opacitiesClasses.includes(classValue)) {
        opacitiesOverlays[classValue] = Object.fromEntries(allProjections.map((p) => [p, true]));
      }
    }
  }

  dispatch(actions.setMicrocalcOverlaysToShow(microcalcOverlays));
  dispatch(actions.setOpacitiesOverlaysToShow(opacitiesOverlays));
};

// Function to get class change tooltip content
export const getClassChangeTooltipContent = (
  projection,
  matched,
  severityChangeStart,
  severityChangeLatest,
  finding_class,
  isClustered,
  t,
  lesionAllInfo,
  matchedLesionAllInfo,
  classes,
  theme
) => {
  const missed = isMissedFinding(lesionAllInfo);
  const originKeys = missed ? getOriginLabelKeys(lesionAllInfo?.detection_origin) : null;

  if (severityChangeStart || severityChangeLatest) {
    return (
      <div>
        {missed && (
          <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
            {t(originKeys.long)}
          </Typography>
        )}
        <Typography className={classes.tooltip_text}>{t(`revision.${finding_class}`)}</Typography>
        {!!severityChangeStart && severityChangeStart !== 0 && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Typography className={classes.tooltip_text}>
              {classTooltip(
                'first',
                finding_class,
                severityChangeStart,
                isClustered,
                t,
                lesionAllInfo?.date_first || matchedLesionAllInfo?.date_first
              )}
            </Typography>
          </>
        )}
        {!!severityChangeLatest && severityChangeLatest !== 0 && (
          <Typography className={classes.tooltip_text}>
            {classTooltip(
              'latest',
              finding_class,
              severityChangeLatest,
              isClustered,
              t,
              lesionAllInfo?.date_latest || matchedLesionAllInfo?.date_latest
            )}
          </Typography>
        )}
      </div>
    );
  }

  if (missed) {
    return (
      <>
        <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
          {t(originKeys.long)}
        </Typography>
        <Typography className={classes.tooltip_text}>{t(`revision.${finding_class}`)}</Typography>
      </>
    );
  }

  return <Typography className={classes.tooltip_text}>{t(`revision.${finding_class}`)}</Typography>;
};

// Filter findings based on selected type, class, and hovered class
export const filterFindings = (findingsToFilter, typeValue, classValue, hoveredClass, hoveredProjections) => {
  if (!findingsToFilter || !Array.isArray(findingsToFilter)) return [];

  return findingsToFilter.filter((finding) => {
    // Filter by type
    if (typeValue !== 'all' && finding.module_type !== typeValue) {
      return false;
    }

    // Filter by class
    if (classValue !== 'all') {
      if (!matchesClassValue(finding, classValue)) {
        if (finding.clustered && finding.nested_items) {
          const hasMatchingNested = finding.nested_items.some((item) => matchesClassValue(item, classValue));
          if (!hasMatchingNested) return false;
        } else if (finding.matched_lesions) {
          const hasMatchingMatched = finding.matched_lesions.some((item) =>
            matchesClassValue(item, classValue)
          );
          if (!hasMatchingMatched) return false;
        } else {
          return false;
        }
      }
    }

    // Filter by hovered class and projections
    if (hoveredClass && hoveredProjections && Array.isArray(hoveredProjections)) {
      const finding_class = finding.finding_class || finding.class;

      // Helper function to extract projection(s) from finding
      const getProjections = (proj) => {
        if (Array.isArray(proj)) {
          return [proj[0]];
        }
        if (typeof proj === 'string' && proj.includes(',')) {
          return proj.split(',').map((p) => p.trim());
        }
        return [proj];
      };

      // Check if finding matches hovered class
      if (!matchesClassValue(finding, hoveredClass)) {
        // Check nested items for clustered findings
        if (finding.clustered && finding.nested_items) {
          const hasMatchingNested = finding.nested_items.some((item) => {
            const itemProj = Array.isArray(item.projection) ? item.projection[0] : item.projection;
            return matchesClassValue(item, hoveredClass) && hoveredProjections.includes(itemProj);
          });
          if (!hasMatchingNested) return false;
        }
        // Check matched_lesions for matched findings
        else if (finding.matched_lesions) {
          const hasMatchingMatched = finding.matched_lesions.some((item) => {
            const itemProj = Array.isArray(item.projection) ? item.projection[0] : item.projection;
            return matchesClassValue(item, hoveredClass) && hoveredProjections.includes(itemProj);
          });
          if (!hasMatchingMatched) return false;
        } else {
          return false;
        }
      } else {
        // Class matches, check projection
        // For clustered findings, check nested items
        if (finding.clustered && finding.nested_items) {
          const hasMatchingNested = finding.nested_items.some((item) => {
            const itemProj = Array.isArray(item.projection) ? item.projection[0] : item.projection;
            return hoveredProjections.includes(itemProj);
          });
          if (!hasMatchingNested) return false;
        }
        // For matched findings, check matched_lesions
        else if (finding.matched_lesions) {
          const hasMatchingMatched = finding.matched_lesions.some((item) => {
            const itemProj = Array.isArray(item.projection) ? item.projection[0] : item.projection;
            return hoveredProjections.includes(itemProj);
          });
          if (!hasMatchingMatched) return false;
        }
        // For regular findings, check main projection
        else {
          const findingProjections = getProjections(finding.projection);
          const hasMatchingProjection = findingProjections.some((proj) => hoveredProjections.includes(proj));
          if (!hasMatchingProjection) return false;
        }
      }
    }

    return true;
  });
};

// Validation and comparison utilities
export const isValidProjectionMatch = (sourceProj, targetProj) => {
  if (!sourceProj || !targetProj) return false;

  const sourceProjection = sourceProj.toLowerCase();
  const targetProjection = targetProj.toLowerCase();

  return sourceProjection[0] === targetProjection[0] && sourceProjection !== targetProjection;
};

// Helper functions for priority comparison
const getBiradsNumericValue = (birads) => {
  if (!birads) return 0;
  if (birads === 'lesionKnown') return 5;
  const match = birads.match(/birads(\d+)/);
  return match ? parseInt(match[1]) : 0;
};

const calculateSizeArea = (size) => {
  if (!size || !Array.isArray(size) || size.length < 2) return 0;
  return size[0] * size[1];
};

export const getHigherPriorityObject = (obj1, obj2) => {
  if (obj1.is_new && !obj2.is_new) return obj2;
  if (!obj1.is_new && obj2.is_new) return obj1;

  if (obj1.birads && obj2.birads) {
    const birads1 = getBiradsNumericValue(obj1.birads);
    const birads2 = getBiradsNumericValue(obj2.birads);
    if (birads1 > birads2) return obj1;
    if (birads2 > birads1) return obj2;
  }

  if (obj1.birads && !obj2.birads) return obj1;
  if (!obj1.birads && obj2.birads) return obj2;

  if (obj1.size && obj2.size) {
    const size1Area = calculateSizeArea(obj1.size);
    const size2Area = calculateSizeArea(obj2.size);
    if (size1Area > size2Area) return obj1;
    if (size2Area > size1Area) return obj2;
  }

  if (obj1.size && !obj2.size) return obj1;
  if (!obj1.size && obj2.size) return obj2;

  return obj1;
};

// Extract all unique classes from findings (including nested and matched items)
export const getAvailableClasses = (findings) => {
  const availableClasses = new Set();
  const validClasses = [
    'vessel',
    'birads2',
    'birads3',
    'birads4',
    'birads5',
    'lesionKnown',
  ];

  if (!findings || !Array.isArray(findings)) {
    return availableClasses;
  }

  findings.forEach((finding) => {
    // Get class from main finding
    const mainClass = finding.finding_class || finding.class;
    if (mainClass && validClasses.includes(mainClass)) {
      availableClasses.add(mainClass);
    }

    // For clustered findings, also check nested_items
    if (finding.clustered && finding.nested_items && Array.isArray(finding.nested_items)) {
      finding.nested_items.forEach((nestedItem) => {
        const nestedClass = nestedItem.finding_class || nestedItem.class;
        if (nestedClass && validClasses.includes(nestedClass)) {
          availableClasses.add(nestedClass);
        }
      });
    }

    // For matched findings, also check matched_lesions
    if (finding.matched_lesions && Array.isArray(finding.matched_lesions)) {
      finding.matched_lesions.forEach((matchedLesion) => {
        const matchedClass = matchedLesion.finding_class || matchedLesion.class;
        if (matchedClass && validClasses.includes(matchedClass)) {
          availableClasses.add(matchedClass);
        }
      });
    }
  });

  return availableClasses;
};

export const getAvailableTypes = (findings) => {
  const availableTypes = new Set();
  const validTypes = ['microcalc', 'opacities'];
  if (!findings || !Array.isArray(findings)) {
    return availableTypes;
  }

  findings.forEach((finding) => {
    const type = finding.module_type;
    if (type && validTypes.includes(type)) {
      availableTypes.add(type);
    }
  });

  return availableTypes;
};

export const getHighestClass = (findings) => {
  if (!findings || !Array.isArray(findings) || findings.length === 0) {
    return '';
  }

  const classesOrder = [
    'lesionKnown',
    'vessel',
    'birads2',
    'birads3',
    'birads4',
    'birads5',
  ];

  const validClasses = [];
  const collectClass = (className) => {
    if (!className) return;

    if (classesOrder.includes(className)) {
      validClasses.push(className);
    }
  };
  const getEffectiveClass = (item) => {
    if (!item) return null;
    return item.finding_class || item.class || null;
  };

  findings.forEach((finding) => {
    if (isMissedFinding(finding)) return;

    // Get class from main finding (check both finding_class and class)
    const mainClass = getEffectiveClass(finding);
    collectClass(mainClass);

    // For clustered findings, also check nested_items
    if (finding.clustered && finding.nested_items && Array.isArray(finding.nested_items)) {
      finding.nested_items.forEach((nestedItem) => {
        if (isMissedFinding(nestedItem)) return;
        const nestedClass = getEffectiveClass(nestedItem);
        collectClass(nestedClass);
      });
    }

    // For matched findings, also check matched_lesions
    if (finding.matched_lesions && Array.isArray(finding.matched_lesions)) {
      finding.matched_lesions.forEach((matchedLesion) => {
        if (isMissedFinding(matchedLesion)) return;
        const matchedClass = getEffectiveClass(matchedLesion);
        collectClass(matchedClass);
      });
    }
  });

  if (validClasses.length === 0) {
    return 'birads1';
  }

  // Find the highest class based on classesOrder index
  return validClasses.reduce((highest, current) => {
    const highestIndex = classesOrder.indexOf(highest);
    const currentIndex = classesOrder.indexOf(current);
    return currentIndex > highestIndex ? current : highest;
  }, validClasses[0]);
};

// Handle filtering lesion type
export const handleFilteringLesionType = (overlayClass, dispatch, actions) => {
  const filterActions = {
    opacities: () => {
      ['birads2', 'birads3', 'birads4', 'birads5', 'lesionKnown'].forEach((type) => {
        dispatch(actions.changeOpacitiesToShow(type, true));
      });
    },
    microcalc: () => {
      ['vessel', 'birads2', 'birads3'].forEach((type) => {
        dispatch(actions.changeMicrocalcToShow(type, false));
      });
      ['birads3', 'birads4', 'birads5'].forEach((type) => {
        dispatch(actions.changeMicrocalcToShow(type, true));
      });
    },
  };

  filterActions[overlayClass]?.();
};
